/**
 * redirectToCheckout(url)
 * ────────────────────────────────────────────────────────────────────────────
 * Safely navigate the user to an external checkout URL (Stripe, PayPal, etc.).
 *
 * Why this exists
 * ──────────────────────────────────────────────────────────────────────────
 * The preview build of this app runs INSIDE an iframe (the Emergent editor).
 * A plain `window.location.href = stripeUrl` therefore only changes the
 * iframe's location. Stripe Checkout sends `X-Frame-Options: DENY`, so the
 * payment page detects it's embedded and refuses to render → the fan sees
 * a blank screen and the iframe bounces back.
 *
 * In production (`startip.es`) the app is the top window, so the old code
 * worked. This util makes BOTH environments behave identically:
 *
 *   1. If we're in an iframe AND we share the same origin as `window.top`
 *      (i.e. the SPA is iframed inside an equivalent host), navigate the
 *      top window — same UX as a direct visit.
 *
 *   2. If we're in a cross-origin iframe (e.g. the Emergent editor),
 *      `window.top.location` would throw `SecurityError`. Fall back to
 *      `window.open(url, "_blank", "noopener,noreferrer")` which always
 *      escapes the frame.
 *
 *   3. If we're NOT in an iframe at all, just use `window.location.assign`.
 *
 * Call it from any checkout handler:
 *
 *   const { data } = await axios.post(...);
 *   redirectToCheckout(data.url ?? data.checkout_url);
 */
export function redirectToCheckout(url) {
  if (!url || typeof url !== "string") return;

  // Case 3 — not embedded: simplest, most reliable navigation.
  if (window.top === window) {
    window.location.assign(url);
    return;
  }

  // Case 1 — same-origin parent: navigate the top window.
  try {
    // Accessing `window.top.location.href` throws on cross-origin iframes.
    // This read is the cheapest way to detect that.
    const _probe = window.top.location.href; // eslint-disable-line no-unused-vars
    window.top.location.href = url;
    return;
  } catch (_) {
    /* fall through */
  }

  // Case 2 — cross-origin iframe: pop a new top-level window.
  // `noopener` keeps the opened tab from referencing back to our app.
  window.open(url, "_blank", "noopener,noreferrer");
}
